// M3-T02 — UI-only gating for whether the viewer may see the assign-owner
// controls. The owner column still displays owners read-only when this is false;
// only the assign/change/remove affordances are hidden.
import { Viewer } from "components/auth/types";
import { UserRole } from "types/gql-types";

// Server enum values (organization.ex); absent/unknown -> permissive default.
export const WHO_CAN_ASSIGN = {
  EVERYONE: "everyone",
  SUPERVISORS_ONLY: "supervisors_only",
} as const;

export const PM_VISIBILITY = {
  ALL: "all",
  ONLY_ASSIGNED: "only_assigned",
} as const;

// Not yet on the generated `getViewer_viewer_organization` type in every schema
// snapshot, so read through this narrow shape.
type OrgWithOwnerSettings = {
  whoCanAssign?: string | null;
  pmVisibility?: string | null;
};

const isSupervisorOrAdmin = (viewer: Viewer): boolean => {
  if (viewer.isAdmin || viewer.isSupervisor) return true;
  const roles = viewer.roles ?? [];
  return roles.includes(UserRole.ADMIN) || roles.includes(UserRole.SUPERVISOR);
};

const isPropertyManager = (viewer: Viewer): boolean => {
  if (viewer.isPropManager) return true;
  const roles = viewer.roles ?? [];
  return roles.includes(UserRole.PROPERTY_MANAGER);
};

/**
 * Admin/supervisor short-circuits to `true` BEFORE the PM restriction, so a
 * dual-role (supervisor + property_manager) viewer is never restricted.
 */
export const deriveCanAssignOwner = (viewer: Viewer): boolean => {
  if (isSupervisorOrAdmin(viewer)) return true;

  const org = viewer.organization as OrgWithOwnerSettings | null;
  const whoCanAssign = org?.whoCanAssign ?? WHO_CAN_ASSIGN.EVERYONE;

  if (whoCanAssign === WHO_CAN_ASSIGN.SUPERVISORS_ONLY) {
    return !isPropertyManager(viewer);
  }

  return true;
};

export const useCanAssignOwner = (viewer: Viewer): boolean => deriveCanAssignOwner(viewer);

// M4-T02 — owner-FILTER gate: same role derivation but keyed on `pm_visibility`,
// NOT `who_can_assign`. The filter is hidden for a PM already restricted to only
// their assigned interests; the data layer scopes their search regardless (M3-T05).
export const deriveCanFilterByOwner = (viewer: Viewer): boolean => {
  if (isSupervisorOrAdmin(viewer)) return true;

  const org = viewer.organization as OrgWithOwnerSettings | null;
  const pmVisibility = org?.pmVisibility ?? PM_VISIBILITY.ALL;

  if (pmVisibility === PM_VISIBILITY.ONLY_ASSIGNED) {
    return !isPropertyManager(viewer);
  }

  return true;
};

export const useCanFilterByOwner = (viewer: Viewer): boolean => deriveCanFilterByOwner(viewer);
